import { IModule, ModuleContent, ModuleParams } from '../types';
import {
  PdfFont,
  PdfBrush,
  PdfSize,
  ETextAlign,
  PdfRect,
  PageGen,
  PdfPen,
  PdfColor,
  PdfUnit,
  PdfPoint,
} from '../../pdf/pdf';
import { getPalette } from '../../pdf/palette';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';

export type FractionType = 'circle' | 'v-square' | 'h-square' | 'annulus';

export interface FractionContent extends ModuleContent {
  numerator: number;
  denominator: number;
  fractionType: FractionType;
}

function parseColor(colorStr: string | undefined, defaultColor: PdfColor): PdfColor {
  if (!colorStr) return defaultColor;
  try {
    return PdfColor.fromHex(colorStr);
  } catch {
    return defaultColor;
  }
}

export const fractionModule: IModule = {
  name: 'fraction',

  parse(source: string, attrs: Record<string, string>): FractionContent {
    const parts = source.trim().split('/');
    const numerator = parseInt(parts[0], 10) || 1;
    const denominator = parseInt(parts[1], 10) || 2;

    const type = (attrs.type as FractionType) || 'circle';

    return {
      type: 'fraction',
      attrs,
      content: source,
      numerator,
      denominator,
      fractionType: type,
    };
  },

  measure(content: FractionContent, params: ModuleParams): PdfSize {
    const width = parseFloat(content.attrs.width) || 50;
    const height = parseFloat(content.attrs.height) || 50;

    return new PdfSize(PdfUnit.FromMillimeters(width), PdfUnit.FromMillimeters(height));
  },

  draw(content: FractionContent, rect: PdfRect, align: ETextAlign, params: ModuleParams): void {
    const page = params.page!;
    if (!page) throw new Error('PageGen required for drawing');

    const { numerator, denominator, fractionType: type, attrs } = content;

    const width = PdfUnit.FromMillimeters(parseFloat(attrs.width) || 50);
    const height = PdfUnit.FromMillimeters(parseFloat(attrs.height) || 50);

    const finalRect = new PdfRect(rect.x, rect.y, width, height);

    let fillColor: PdfColor;
    if (attrs.palette) {
      const palette = getPalette(attrs.palette);
      const paletteIndex = parseInt(attrs['palette-index'] || '0', 10);
      const hexColor = palette.colors[paletteIndex % palette.colors.length] || palette.colors[0];
      fillColor = PdfColor.fromHex(hexColor);
    } else if (attrs.color) {
      fillColor = parseColor(attrs.color, PdfColor.fromHex('#FF0000'));
    } else {
      fillColor = parseColor(attrs.color, PdfColor.fromHex('#FF0000'));
    }

    const strokeColor = parseColor(attrs.stroke, PdfColor.fromHex('#000000'));
    const strokeWidth = PdfUnit.FromMillimeters(parseFloat(attrs['stroke-width']) || 0.5);

    const showLabel = attrs['show-label'] !== undefined || attrs.label !== undefined;
    const labelColor = parseColor(attrs['label-color'], PdfColor.fromHex('#000000'));

    const pen = new PdfPen(strokeColor, strokeWidth);
    const brush = new PdfBrush(fillColor);
    const labelBrush = new PdfBrush(labelColor);

    const fontSize = Math.min(width, height) * 0.25;
    const font = new PdfFont(PdfFont.FONT_NAME, fontSize);

    switch (type) {
      case 'circle':
        drawCircleFraction(
          page,
          pen,
          brush,
          finalRect,
          numerator,
          denominator,
          showLabel,
          font,
          labelBrush
        );
        break;
      case 'v-square':
        drawVSquareFraction(
          page,
          pen,
          brush,
          finalRect,
          numerator,
          denominator,
          showLabel,
          font,
          labelBrush
        );
        break;
      case 'h-square':
        drawHSquareFraction(
          page,
          pen,
          brush,
          finalRect,
          numerator,
          denominator,
          showLabel,
          font,
          labelBrush
        );
        break;
      case 'annulus':
        drawAnnulusFraction(
          page,
          pen,
          brush,
          finalRect,
          numerator,
          denominator,
          showLabel,
          font,
          labelBrush
        );
        break;
    }
  },
};

function drawCircleFraction(
  page: PageGen,
  pen: PdfPen,
  brush: PdfBrush,
  rect: PdfRect,
  numerator: number,
  denominator: number,
  showLabel: boolean,
  font: PdfFont,
  labelBrush: PdfBrush
): void {
  const fullAngle = 360;
  const filledAngle = (numerator / denominator) * fullAngle;
  const startAngle = -90;
  const endAngle = startAngle + filledAngle;

  if (filledAngle > 0 && filledAngle < fullAngle) {
    page.drawPie(pen, brush, rect, startAngle, endAngle);
  } else if (filledAngle >= fullAngle) {
    page.drawEllipse(pen, brush, rect);
  }

  page.drawEllipse(pen, null, rect);

  if (showLabel) {
    drawFractionLabel(page, rect, numerator, denominator, font, labelBrush, pen);
  }
}

function drawVSquareFraction(
  page: PageGen,
  pen: PdfPen,
  brush: PdfBrush,
  rect: PdfRect,
  numerator: number,
  denominator: number,
  showLabel: boolean,
  font: PdfFont,
  labelBrush: PdfBrush
): void {
  const filledHeight = (numerator / denominator) * rect.height;
  const filledRect = new PdfRect(rect.x, rect.y, rect.width, filledHeight);

  if (filledHeight > 0) {
    page.drawRectangle(pen, brush, filledRect);
  }

  page.drawRectangle(pen, null, rect);

  if (showLabel) {
    drawFractionLabel(page, rect, numerator, denominator, font, labelBrush, pen);
  }
}

function drawHSquareFraction(
  page: PageGen,
  pen: PdfPen,
  brush: PdfBrush,
  rect: PdfRect,
  numerator: number,
  denominator: number,
  showLabel: boolean,
  font: PdfFont,
  labelBrush: PdfBrush
): void {
  const filledWidth = (numerator / denominator) * rect.width;
  const filledRect = new PdfRect(rect.x, rect.y, filledWidth, rect.height);

  if (filledWidth > 0) {
    page.drawRectangle(pen, brush, filledRect);
  }

  page.drawRectangle(pen, null, rect);

  if (showLabel) {
    drawFractionLabel(page, rect, numerator, denominator, font, labelBrush, pen);
  }
}

function drawAnnulusFraction(
  page: PageGen,
  pen: PdfPen,
  brush: PdfBrush,
  rect: PdfRect,
  numerator: number,
  denominator: number,
  showLabel: boolean,
  font: PdfFont,
  labelBrush: PdfBrush
): void {
  const outerRadius = Math.min(rect.width, rect.height) / 2;
  const innerRadiusRatio = 0.5;

  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;

  const fullAngle = 360;
  const filledAngle = (numerator / denominator) * fullAngle;
  const startAngle = -90;
  const endAngle = startAngle + filledAngle;

  const outerRect = new PdfRect(
    centerX - outerRadius,
    centerY - outerRadius,
    outerRadius * 2,
    outerRadius * 2
  );

  // Draw annular sector (ring segment) - this is the proper way to draw colored annulus portion
  if (filledAngle > 0 && filledAngle < fullAngle) {
    page.drawAnnularSector(pen, brush, outerRect, innerRadiusRatio, startAngle, endAngle);
  } else if (filledAngle >= fullAngle) {
    // Full annulus - draw filled outer ellipse with white inner ellipse
    page.drawEllipse(pen, brush, outerRect);
    const innerRadius = outerRadius * innerRadiusRatio;
    const innerRect = new PdfRect(
      centerX - innerRadius,
      centerY - innerRadius,
      innerRadius * 2,
      innerRadius * 2
    );
    page.drawEllipse(null, PdfBrush.White, innerRect);
  }

  // Draw outline circles
  const innerRadius = outerRadius * innerRadiusRatio;
  const innerRect = new PdfRect(
    centerX - innerRadius,
    centerY - innerRadius,
    innerRadius * 2,
    innerRadius * 2
  );
  page.drawEllipse(pen, null, outerRect);
  page.drawEllipse(pen, null, innerRect);

  if (showLabel) {
    // Draw proper fraction with numerator, line, and denominator
    const fontSize = font.size;
    const lineThickness = Math.max(1, fontSize * 0.08);

    const numText = String(numerator);
    const denText = String(denominator);
    const numFont = new PdfFont(font.font, fontSize * 0.7);

    const numWidth = page.measureString(numText, numFont).width;
    const denWidth = page.measureString(denText, numFont).width;
    const textWidth = Math.max(numWidth, denWidth);
    const padding = fontSize * 0.25;
    const lineWidth = textWidth + padding * 2;

    const lineY = centerY;
    const lineXStart = centerX - lineWidth / 2;

    const bgBrush = new PdfBrush(PdfColor.fromRgb(255, 255, 255, 0.85));
    const bgRect = new PdfRect(
      centerX - lineWidth / 2 - padding * 0.5,
      centerY - fontSize * 0.85 - padding * 0.5,
      lineWidth + padding,
      fontSize * 1.5 + lineThickness + padding
    );
    page.drawRectangle(null, bgBrush, bgRect);

    // Draw fraction line
    page.drawLine(
      pen,
      new PdfPoint(lineXStart, lineY),
      new PdfPoint(lineXStart + lineWidth, lineY)
    );

    // Draw numerator above the line
    const numRect = new PdfRect(
      centerX - lineWidth / 2,
      lineY - fontSize * 0.85,
      lineWidth,
      fontSize
    );
    page.drawString(numText, numFont, labelBrush, null, numRect, ETextAlign.Center);

    // Draw denominator below the line
    const denRect = new PdfRect(
      centerX - lineWidth / 2,
      lineY + lineThickness,
      lineWidth,
      fontSize
    );
    page.drawString(denText, numFont, labelBrush, null, denRect, ETextAlign.Center);
  }
}

function drawFractionLabel(
  page: PageGen,
  rect: PdfRect,
  numerator: number,
  denominator: number,
  font: PdfFont,
  labelBrush: PdfBrush,
  pen: PdfPen
): void {
  const {centerX} = rect;
  const {centerY} = rect;
  const fontSize = font.size;
  const lineThickness = Math.max(1, fontSize * 0.08);

  const numText = String(numerator);
  const denText = String(denominator);
  const numFont = new PdfFont(font.font, fontSize * 0.7);

  const numWidth = page.measureString(numText, numFont).width;
  const denWidth = page.measureString(denText, numFont).width;
  const textWidth = Math.max(numWidth, denWidth);
  const padding = fontSize * 0.25;
  const lineWidth = textWidth + padding * 2;

  const lineY = centerY;
  const lineXStart = centerX - lineWidth / 2;

  const bgBrush = new PdfBrush(PdfColor.fromRgb(255, 255, 255, 0.85));
  const bgRect = new PdfRect(
    centerX - lineWidth / 2 - padding * 0.5,
    centerY - fontSize * 0.85 - padding * 0.5,
    lineWidth + padding,
    fontSize * 1.5 + lineThickness + padding
  );
  page.drawRectangle(null, bgBrush, bgRect);

  page.drawLine(pen, new PdfPoint(lineXStart, lineY), new PdfPoint(lineXStart + lineWidth, lineY));

  const numRect = new PdfRect(
    centerX - lineWidth / 2,
    lineY - fontSize * 0.85,
    lineWidth,
    fontSize
  );
  page.drawString(numText, numFont, labelBrush, null, numRect, ETextAlign.Center);

  const denRect = new PdfRect(centerX - lineWidth / 2, lineY + lineThickness, lineWidth, fontSize);
  page.drawString(denText, numFont, labelBrush, null, denRect, ETextAlign.Center);
}
